import { HttpClient } from '@/components/shared/http-client'
import store from '@/store/store'
import { getApiHostname } from '@/components/shared/api'

export function performLogout() {
  store.dispatch('logout')
}

export async function refreshTokenInternal() {
  try {
    const response = await refreshToken()
    if (response.status !== 200) performLogout()
  } catch (error) {
    performLogout()
  }
}

export async function refreshToken() {
  const response = await HttpClient.post('/auth/refresh')
  return response
}

// This is a little bit of magic that lets us call /refresh only once,
// and after the refresh, it retries the original request
export async function refreshTokenStore(store) {
  if (store.state.auth.isRefreshing) {
    return store.state.auth.refreshingCall
  }
  store.commit('SET_AUTH_IS_REFRESHING', true)
  const refreshingCall = HttpClient.post('/auth/refresh').then(() => {
    store.commit('SET_AUTH_IS_REFRESHING', false)
    store.commit('SET_AUTH_REFRESH_CALL', undefined)
    return Promise.resolve(true)
  })

  store.commit('SET_AUTH_REFRESH_CALL', refreshingCall)
  return refreshingCall
}

export async function getAuthenticatedUser() {
  try {
    const response = await getMyPermissions()
    if (response.status === 200) {
      await refreshTokenInternal()
      const intervalName = setInterval(async () => {
        await refreshTokenInternal()
      }, intervalMilliSeconds)

      await store.dispatch('setRefreshTokenIntervalName', intervalName)
    } else {
      performLogout()
    }
  } catch (error) {
    console.error(error)
    performLogout()
  }
}

export async function sendTfaVerification(tfaPhone, tfaType) {
  try {
    const request = { tfaPhone: tfaPhone, tfaType: tfaType }
    return await HttpClient.post('/tfa/send-verification', request)
  } catch (error) {
    return error.response.data
  }
}

export async function verifyTfaCode(verifyCode, tfaPhone) {
  try {
    const request = { code: verifyCode, tfaPhone: tfaPhone }
    return await HttpClient.post('/tfa/verify-code', request)
  } catch (error) {
    return error.response.data
  }
}

export async function saveTfaSettings(request) {
  try {
    const response = await HttpClient.put('api/users/tfa/update', request)
    if (response.status === 200) {
      setGlobalAlert('Successfully saved TFA settings')
    }
    return response
  } catch (error) {
    return error.response.data
  }
}

export async function getMyPermissions() {
  const response = await HttpClient.get('api/users/me')
  if (response.status === 200) {
    const userProfile = response.data
    await store.dispatch('newLogin', userProfile)
    await loadAccountIfOnlyOneAccount(userProfile)
  }

  setTimeout(() => connectToServerEvents(), 1500)

  return response
}
async function loadAccountIfOnlyOneAccount(userProfile) {
  const isAnAdmin = userProfile.isAnAdmin
  const brokerages = userProfile.brokerRoles ? Object.keys(userProfile.brokerRoles) : []
  const isBrokerUser = brokerages.length > 0
  if (isAnAdmin || isBrokerUser) {
    return
  }
  const attachedAccountCodes = userProfile.accountRoles ? Object.keys(userProfile.accountRoles) : []
  if (attachedAccountCodes && attachedAccountCodes.length === 1) {
    const accountCode = attachedAccountCodes[0]
    await loadAccountAndFeaturesAndPreferences(accountCode)
  }
}

export async function loadAccountAndFeaturesAndPreferences(accountCode) {
  await loadAccount(accountCode)
  await loadFeatures(accountCode)
  await loadPreferences(accountCode)
}
async function loadAccount(accountCode) {
  const response = await loadAccountApi(accountCode)
  const account = response.data.account

  await store.dispatch('setActiveAccount', {
    ...account,
  })
}
async function loadFeatures(accountCode) {
  const response = await loadAccountFeaturesApi(accountCode)
  await store.dispatch('setFeatureFlags', response.data)
}
async function loadPreferences(accountCode) {
  const response = await loadAccountPreferencesApi(accountCode)
  await store.dispatch('setPreferences', response.data)
}

// eslint-disable-next-line no-unused-vars
function connectToServerEvents() {
  const url = `${getApiHostname()}/server-events`
  const source = new EventSource(url, {
    withCredentials: true,
  })

  // eslint-disable-next-line no-unused-vars
  source.onopen = event => {}
  source.addEventListener('notification', async event => {
    await handleNotificationEvent(JSON.parse(event.data))
  })
  // Wizard Events
  source.addEventListener('wizard-upload', async event => {
    const payload = JSON.parse(event.data)
    await handleWizardUploadEvent(payload)
  })
  // Admin Conversion
  source.addEventListener('admin-conversion', async event => {
    const payload = JSON.parse(event.data)
    await handleAdminConversionEvent(payload)
  })

  source.addEventListener(
    'error',
    event => {
      if (event.readyState == EventSource.CLOSED) {
        console.log('Event was closed')
        console.log(EventSource)
      }
    },
    false
  )
  // eslint-disable-next-line no-unused-vars
  source.onerror = event => {
    console.log('Connection closed')
  }
}

async function handleAdminConversionEvent(eventData) {
  const {accountsProcessed, successfully, withError, complete} = eventData
  const payload = {
    numProcessed: accountsProcessed,
    numSuccessful: successfully,
    numError: withError,
    isComplete: complete,
  }
  await store.dispatch('adminConversion/updateProgress', payload)
}

async function handleWizardUploadEvent(eventData) {
  const progress = eventData.progress
  const message = eventData.message
  const httpStatus = eventData.httpStatus
  const responses = eventData.responses
  const errorDto = eventData.errorDto

  const payload = {
    wizardUploadMessage: message,
    wizardUploadProgress: progress,
  }
  await store.dispatch('wizardUploadProgress/updateProgress', payload)
  if (progress === 100) {
    const isBackground = httpStatus === 202
    const isSuccess = httpStatus > 199 && httpStatus < 300 && !isBackground
    if (isSuccess) {
      const fn = store.getters['wizardUploadProgress/getSuccessFn']
      if (fn) {
        fn({
          status: httpStatus,
          data: responses,
        })
      }
    } else if (isBackground) {
      const fn = store.getters['wizardUploadProgress/getBackgroundFn']
      if (fn) {
        fn({
          status: httpStatus,
          data: responses,
        })
      }
    } else {
      const fn = store.getters['wizardUploadProgress/getErrorFn']
      if (fn) {
        fn({
          response: {
            status: httpStatus,
            data: errorDto,
          },
        })
      }
    }
    await store.dispatch('wizardUploadProgress/showDialog', false)
  }
}

async function handleNotificationEvent(eventData) {
  const msg = eventData.message
  const note = eventData.note
  const link = eventData.link

  const payload = {
    details: msg,
    notes: note, // Optional
    link: link, // Optional
  }
  await store.dispatch('addAccountNotification', payload)
}

export function setGlobalAlert(alert) {
  store
    .dispatch('setGlobalAlert', {
      show: true,
      text: alert,
    })
    .then(() => {})
}

export function setGlobalErrorAlert(alert) {
  store
    .dispatch('setGlobalErrorAlert', {
      show: true,
      text: alert,
    })
    .then(() => {})
}

export async function loadAccountApi(accountCode) {
  const response = await HttpClient.get(`/api/accounts/${accountCode}`)
  return response
}
export async function loadAccountFeaturesApi(accountCode) {
  const response = await HttpClient.get(`/api/accounts/${accountCode}/features`)
  return response
}
export async function loadAccountPreferencesApi(accountCode) {
  const response = await HttpClient.get(`/api/accounts/${accountCode}/preferences`)
  return response
}

const oneSecondInMillis = 1000
const oneMinute = oneSecondInMillis * 60
const fourteenMinutes = oneMinute * 14
export const intervalMilliSeconds = fourteenMinutes
